import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, BadgeCheck, ExternalLink, GitBranch, Loader2, MoreHorizontal, RefreshCw, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import {
  CONFIRMATION_HINTS, GITHUB_KIND_LABELS, GITHUB_STATE_LABELS, RELEASE_PRODUCT_LABELS, formatDate, formatDateTime, productErrorMessage, proofText, releaseConfirmation,
} from "../lib/product";
import useRunningVersions from "../hooks/useRunningVersions";
import { ConfirmationBadge, ReleaseStateBadge, StatusBadge, TypeBadge } from "../components/product/ProductBits";
import { GithubDialog } from "../components/product/ProductDialogs";
import { AttachItemDialog, CorrectionDialog, DeliverDialog, ReleaseFormDialog } from "../components/product/ReleaseDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

function SideRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1.5">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-slate-900 min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function ProductReleaseDetail() {
  const { releaseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [release, setRelease] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [dialog, setDialog] = useState(null);
  const [removing, setRemoving] = useState("");
  const running = useRunningVersions();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/product/releases/${releaseId}`);
      setRelease(data);
      setPhase("ready");
    } catch (e) {
      setErrorText(e?.response?.status === 404 ? "Cette version n'existe pas." : productErrorMessage(e, "Impossible de charger la version."));
      setPhase("error");
    }
  }, [releaseId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  const applied = (data, message) => {
    if (!data) { load(); return; }
    setRelease(data);
    setDialog(null);
    if (message) toast.success(message);
  };

  const remove = async (kind, ref) => {
    setRemoving(ref);
    try {
      const { data } = kind === "item" ? await api.delete(`/product/releases/${release.id}/items/${ref}`)
        : await api.delete(`/product/releases/${release.id}/github`, { params: { url: ref } });
      applied(data, kind === "item" ? "Élément retiré de la version" : "Lien retiré");
    } catch (e) {
      toast.error(productErrorMessage(e, "Impossible de retirer cet élément."));
      load();
    } finally {
      setRemoving("");
    }
  };

  if (phase === "loading") return <div className="space-y-3" data-testid="release-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="release-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/produit/versions">Retour aux versions</Link></Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      </Card>
    );
  }

  const caps = release.capabilities || {};
  const planned = release.state === "planned";
  const delivered = !planned;

  return (
    <div className="space-y-4" data-testid="release-detail">
      <Link to="/produit/versions" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Versions livrées</Link>

      <Card className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="text-xs text-slate-500 tabular-nums" data-testid="release-number">{release.number} · {RELEASE_PRODUCT_LABELS[release.product]}</div>
            <h1 className="font-display font-bold text-xl text-slate-900 break-words" data-testid="release-title">Version {release.label}</h1>
            <div className="flex flex-wrap gap-1.5 pt-1"><ReleaseStateBadge state={release.state} /><ConfirmationBadge confirmation={releaseConfirmation(release, running.confirmedIds)} /></div>
          </div>
          {(caps.manage || (planned && hasPermission(user, "product:delete"))) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="release-more"><MoreHorizontal size={18} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Actions sur la version">
                {caps.edit && <DropdownMenuItem onClick={() => setDialog("edit")} data-testid="release-action-edit">Modifier</DropdownMenuItem>}
                {planned && hasPermission(user, "product:delete") && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="release-action-delete">Supprimer la version…</DropdownMenuItem>}
                {caps.attach && <DropdownMenuItem onClick={() => setDialog("attach")} data-testid="release-action-attach">Ajouter un élément en test</DropdownMenuItem>}
                {delivered && <DropdownMenuItem onClick={() => setDialog("correction")} data-testid="release-action-correction">Ajouter une correction</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => setDialog("github")} data-testid="release-action-github">Ajouter un lien GitHub</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {caps.deliver && <div><Button onClick={() => setDialog("deliver")} className="w-full sm:w-auto" data-testid="release-deliver">Livrer cette version</Button></div>}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4 min-w-0">
          {release.notes && (
            <Card className="p-5"><h2 className="text-sm font-semibold text-slate-900">Notes de version</h2>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-line break-words" data-testid="release-notes-text">{release.notes}</p></Card>
          )}

          <Card className="p-5 space-y-3" data-testid="release-items">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Éléments de la version ({release.items.length})</h2>
              {caps.attach && <Button variant="ghost" size="sm" onClick={() => setDialog("attach")} data-testid="release-attach">Ajouter</Button>}
            </div>
            {release.items.length === 0 ? (
              <p className="text-sm text-slate-500">{planned ? "Aucun élément. Rattachez ceux qui sont « en test » pour les livrer ensemble." : "Aucun élément n'a été livré avec cette version."}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {release.items.map((i) => (
                  <li key={i.id} className="py-2 flex items-start justify-between gap-2" data-testid={`release-item-${i.number}`}>
                    <div className="min-w-0 space-y-1">
                      <Link to={`/produit/${i.id}`} className="text-sm hover:underline break-words"><span className="text-xs text-slate-500 tabular-nums">{i.number}</span> {i.title}</Link>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={i.status} /><TypeBadge type={i.type} />
                        {i.verified && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><BadgeCheck size={13} aria-hidden="true" />Vérifié</span>}
                      </div>
                    </div>
                    {caps.attach && !(delivered && i.status === "delivered") && (
                      <button type="button" aria-label={`Retirer ${i.number}`} disabled={removing === i.id} onClick={() => remove("item", i.id)} className="text-slate-400 hover:text-slate-700 shrink-0 mt-1">
                        {removing === i.id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {release.corrections.length > 0 && (
            <Card className="p-5 space-y-3" data-testid="release-corrections">
              <h2 className="text-sm font-semibold text-slate-900">Corrections</h2>
              <ol className="space-y-3">
                {release.corrections.map((c, index) => (
                  <li key={index} className="text-sm">
                    <p className="text-slate-800 whitespace-pre-line break-words">{c.text}</p>
                    <p className="text-xs text-slate-500">{c.by_name || "Un compte"} · {formatDateTime(c.at)}</p>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-5 divide-y divide-slate-100" data-testid="release-summary">
            <SideRow label="Logiciel">{RELEASE_PRODUCT_LABELS[release.product]}</SideRow>
            <SideRow label="Preuve">{release.deploy_ref ? <span className="font-mono" title={release.deploy_ref} data-testid="release-proof">{proofText(release)}</span> : <span className="text-slate-400">Pas encore livrée</span>}</SideRow>
            <SideRow label="Déployée le">{release.deployed_at ? formatDate(release.deployed_at) : <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Déclarée par">{release.delivered_by_name || <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Créée">{release.created_by_name} · {formatDate(release.created_at)}</SideRow>
            {release.product === "crm" && release.running?.crm && <SideRow label="Commit du CRM en cours"><span className="font-mono">{release.running.crm.slice(0, 7)}</span></SideRow>}
          </Card>
          {delivered && (
            <Card className="p-5 text-sm text-slate-600" data-testid="release-confirmation-hint">
              <ConfirmationBadge confirmation={releaseConfirmation(release, running.confirmedIds)} testid="release-confirmation-side" />
              <p className="mt-2">{CONFIRMATION_HINTS[releaseConfirmation(release, running.confirmedIds)]}</p>
            </Card>
          )}
          <Card className="p-5 space-y-2" data-testid="release-github">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><GitBranch size={14} className="text-slate-400" />GitHub</h2>
              {caps.manage && <Button variant="ghost" size="sm" onClick={() => setDialog("github")}>Ajouter</Button>}
            </div>
            {release.github.length === 0 ? <p className="text-sm text-slate-500">Aucun lien.</p> : (
              <ul className="space-y-1.5">
                {release.github.map((g) => (
                  <li key={g.url} className="flex items-start justify-between gap-2">
                    <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-sm text-jamora hover:underline min-w-0 break-words inline-flex items-start gap-1">
                      <ExternalLink size={12} className="mt-1 shrink-0" aria-hidden="true" />
                      <span>{GITHUB_KIND_LABELS[g.kind]} {g.repo}{g.ref ? ` ${g.kind === "issue" || g.kind === "pull" ? "#" : ""}${g.ref}` : ""}{g.state && <span className="text-xs text-slate-500"> · {GITHUB_STATE_LABELS[g.state]}</span>}</span>
                    </a>
                    {caps.manage && (
                      <button type="button" aria-label="Retirer ce lien" disabled={removing === g.url} onClick={() => remove("github", g.url)} className="text-slate-400 hover:text-slate-700 shrink-0">
                        {removing === g.url ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </div>

      <ReleaseFormDialog release={release} open={dialog === "edit"} onOpenChange={(open) => setDialog(open ? "edit" : null)} onDone={(data) => applied(data, "Version modifiée")} />
      <DeliverDialog release={release} open={dialog === "deliver"} onOpenChange={(open) => setDialog(open ? "deliver" : null)}
        onDone={(data) => applied(data, data.outcome?.items_delivered ? `Version livrée : ${data.outcome.items_delivered} élément${data.outcome.items_delivered > 1 ? "s" : ""} livré${data.outcome.items_delivered > 1 ? "s" : ""}` : "Version livrée")} />
      <CorrectionDialog release={release} open={dialog === "correction"} onOpenChange={(open) => setDialog(open ? "correction" : null)} onDone={(data) => applied(data, "Correction ajoutée")} />
      <AttachItemDialog release={release} open={dialog === "attach"} onOpenChange={(open) => setDialog(open ? "attach" : null)} onDone={(data) => applied(data, "Élément rattaché")} />
      <GithubDialog item={release} basePath="/product/releases" open={dialog === "github"} onOpenChange={(open) => setDialog(open ? "github" : null)} onDone={(data) => applied(data, "Lien ajouté")} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? release : null} testId="delete-product" onClose={() => setDialog(null)}
        title={`Supprimer la version ${release.number} ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une version qui a des retours rattachés ou qui est livrée n'est jamais supprimée."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/product/releases/${release.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/produit/versions");
          } catch (e) {
            toast.error(productErrorMessage(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
    </div>
  );
}
